import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Flex } from '@react-spectrum/layout';
import { View } from '@react-spectrum/view';
import { KeyboardActions } from 'app/stores/keyboard';
import { useAppDispatch } from 'app/stores/store';
import { ViewportActions } from 'app/stores/viewport/actions';
import { Project } from 'app/views/components/editor/project/Project';
import { Viewport } from 'app/views/components/editor/Viewport';
import { useResize } from 'app/views/hooks/useResize';
import { useShortcuts } from 'app/views/hooks/useShortcuts';
import { hasClipboard } from 'app/utils/caniuse';
import { Capabilities } from 'app/utils/Capabilities';
const AbsoluteFillStyles = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: '0',
    left: '0',
};
const staticShortcuts = [
    {
        keys: ['ctrl+c', 'meta+c'],
        handler: 'copy-to-buffer',
        isEnabled: () => true,
    },
    {
        keys: ['ctrl+v', 'meta+v'],
        handler: 'paste-from-buffer',
        isEnabled: () => true,
    },
];
export const Workspace = (props) => {
    const containerRef = React.useRef(null);
    const mouseOverlayRef = React.useRef(null);
    const dispatch = useAppDispatch();
    const copyToClipboardShortcut = useSelector((_state) => configGet('shortcutsCopyToClipboard', 'meta+c'));
    const shortcuts = React.useMemo(() => [
        {
            keys: copyToClipboardShortcut,
            handler: 'copy-to-clipboard',
            isEnabled: () => hasClipboard(),
        },
    ].concat(staticShortcuts), [copyToClipboardShortcut]);
    // -----------------------
    // Scroller
    // -----------------------
    const currentOverlayRef = mouseOverlayRef.current;
    const getScrollTarget = React.useCallback(() => currentOverlayRef === null || currentOverlayRef === void 0 ? void 0 : currentOverlayRef.UNSAFE_getDOMNode(), [currentOverlayRef]);
    // -----------------------
    // Resize
    // -----------------------
    const getResizeElement = useCallback(() => { var _a; return (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.UNSAFE_getDOMNode(); }, [containerRef]);
    const onResize = useCallback((bounds) => {
        dispatch(ViewportActions.setCanvasRect(bounds));
    }, [dispatch]);
    useResize(getResizeElement, onResize);
    // -----------------------
    // Keyboard shortcuts
    // -----------------------
    const shortcutHandlers = useCallback((event, _, shortcut) => {
        const { keys, handler } = shortcut;
        dispatch(KeyboardActions.shortcut({
            event,
            keys: Array.isArray(keys) ? keys.join('|') : keys,
            command: String(handler),
        }));
    }, [dispatch]);
    useShortcuts(shortcuts, shortcutHandlers);
    // -----------------------
    // Render
    // -----------------------
    return (React.createElement(Flex, { id: "editor-container", UNSAFE_className: Capabilities.shared.isMobileDevice ? 'is-mobile' : 'is-desktop', height: "100%", width: "100%", ref: containerRef, alignItems: "stretch", justifyContent: "stretch", position: "relative", flex: 1 },
        React.createElement(View, { position: "relative", backgroundColor: "gray-200", flex: "1", overflow: "hidden" },
            React.createElement(View, { UNSAFE_className: "user-select-none touch-action-none", UNSAFE_style: AbsoluteFillStyles },
                React.createElement(Viewport, { scrollTarget: getScrollTarget },
                    React.createElement(Project, null))),
            React.createElement(View, { UNSAFE_style: AbsoluteFillStyles, ref: mouseOverlayRef }, props.children),
            React.createElement("div", { className: "text-measurer", style: { position: 'absolute' } }))));
};
